/**
 * RecurringTransactionService
 * Service for RecurringTransaction entity operations
 * Route: api/recurring-transaction
 * Controller: RecurringTransactionController.cs
 */
import { CrudService } from '../base/CrudService';
/**
 * RecurringTransactionService
 * Handles recurring transaction CRUD operations and model view queries
 * Maps to /api/recurring-transaction endpoint
 */
export class RecurringTransactionService extends CrudService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? RecurringTransactionService.DEFAULT_SERVICE_PATH,
            basePath: RecurringTransactionService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // Override getById to support GUID
    async getById(id) {
        const response = await this.getByIdRaw(id);
        if (!response.data) {
            throw new Error(`Recurring transaction with ID ${id} not found`);
        }
        return response.data;
    }
    getByIdRaw(id) {
        const url = this.buildUrl(id);
        return this.http.get(url);
    }
    // ==================== CACHE INVALIDATION ====================
    /**
     * Invalidate recurring transaction cache
     * POST /api/recurring-transaction/cache/invalidate
     */
    async invalidateCache() {
        const response = await this.invalidateCacheRaw();
        return response.data ?? false;
    }
    /**
     * Invalidate recurring transaction cache (raw)
     */
    invalidateCacheRaw() {
        const endpoint = this.getEndpoint('cache-invalidate', 'cache/invalidate');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, {});
    }
    // ==================== MODEL VIEW ENDPOINTS ====================
    /**
     * Get recurring transaction model view by ID
     * GET /api/recurring-transaction/view/{id}
     */
    async getView(id) {
        const response = await this.getViewRaw(id);
        if (!response.data) {
            throw new Error(`Recurring transaction view with ID ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get recurring transaction model view by ID (raw)
     */
    getViewRaw(id) {
        const url = this.buildUrl(`view/${id}`);
        return this.http.get(url);
    }
    /**
     * Get all recurring transaction model views
     * GET /api/recurring-transaction/view
     */
    async getAllViews() {
        const response = await this.getAllViewsRaw();
        return response.data ?? [];
    }
    /**
     * Get all recurring transaction model views (raw)
     */
    getAllViewsRaw() {
        const url = this.buildUrl('view');
        return this.http.get(url);
    }
    /**
     * Get recurring transaction views by payer
     * GET /api/recurring-transaction/payer/{payerId}/view
     */
    async getViewsByPayer(payerId) {
        const response = await this.getViewsByPayerRaw(payerId);
        return response.data ?? [];
    }
    /**
     * Get recurring transaction views by payer (raw)
     */
    getViewsByPayerRaw(payerId) {
        const url = this.buildUrl(`payer/${payerId}/view`);
        return this.http.get(url);
    }
}
RecurringTransactionService.DEFAULT_SERVICE_PATH = 'payments';
RecurringTransactionService.BASE_PATH = 'api/recurring-transaction';
//# sourceMappingURL=RecurringTransactionService.js.map